/**
 * AI Affiliate Conversion Tracking Hook
 *
 * If the current browser session was detected as arriving from an AI
 * assistant (see aiCitationTracker.trackAICitation), this listens for clicks
 * on affiliate links and records them as a conversion tied to that AI source.
 *
 * No-ops entirely for sessions with no AI attribution — general affiliate
 * click analytics are already covered by useGlobalOutboundTracking (GA4).
 */

import { useEffect } from 'react';
import { getAISessionAttribution, trackAffiliateClick } from '../utils/aiCitationTracker';

function detectAffiliatePlatform(link, domain) {
  const declared = link.dataset.affiliatePlatform?.trim().toLowerCase();
  if (declared) return declared;

  if (domain.includes('amazon.')) {
    return domain.includes('amazon.ca') ? 'amazon-ca' : 'amazon-us';
  }
  if (domain.includes('etsy.') || domain.includes('shopify.') ||
      domain.includes('walmart.') || domain.includes('target.')) {
    return domain.split('.')[0];
  }
  if (link.rel?.includes('sponsored')) {
    return domain;
  }
  return null;
}

export const useAIAffiliateConversionTracking = () => {
  useEffect(() => {
    const handleClick = (e) => {
      // Cheap short-circuit: skip all the DOM work below for the vast
      // majority of clicks that happen outside an AI-attributed session.
      if (!getAISessionAttribution()) return;

      const link = e.target.closest('a');
      if (!link || !link.href) return;

      let url;
      try {
        url = new URL(link.href);
      } catch {
        return;
      }
      if (!url.protocol.startsWith('http')) return;
      if (url.hostname === window.location.hostname || url.hostname.includes('originselect')) return;

      const platform = detectAffiliatePlatform(link, url.hostname.toLowerCase());
      if (!platform) return;

      trackAffiliateClick({
        platform,
        url: link.href,
        linkText: link.textContent?.trim().substring(0, 100) || '',
      });

      console.log(`🛒 AI-attributed affiliate click: ${platform}`, link.href);
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, []);
};

export default useAIAffiliateConversionTracking;
